import { Dialog as MuiDialog, Paper, Box } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { aiAssistantContext, AIAssistantDisplayMode } from '../../../state/AIAssistantContextProvider';
import { selectUser } from '../../../reducers/user.selectors';
import useScreenSize from '../../../utils/useScreenSize';
import { backgroundGradients, borderRadius, boxShadows }
  from '../../../assets/styles/themes/tokens';
import AIAssistantChat from '../shared/Chat/AIAssistantChat';
import AIAssistantFeedbackDialog from '../../modals/FeedbackDialogs/AIAssistantFeedbackDialog';
import AIOnlyFeedbackDialog from '../../modals/FeedbackDialogs/AIOnlyFeedbackDialog';
import AIAssistantDraggable from './AIAssistantDraggable';
import AIAssistantDialogTitle from './AIAssistantDialogTitle';
import AIAssistantUnitEntry from './Views/AIAssistantUnitEntry';
import { AIAssistantAlerts } from '../shared/AIAssistantAlerts';
import AIAssistantViewRouter, { type ViewMap } from '../shared/AIAssistantViewRouter';
import { INITIAL_POSITION, ActiveView, DIALOG_WIDTH } from '../shared/constants';
import 
AIAssistantEditorProvider, 
{ aiAssistantEditorContext } from '../../../state/AIAssistantEditorProvider';

const DIALOG_VIEW_MAP: ViewMap = {
  [ActiveView.CHAT]: AIAssistantChat,
  [ActiveView.UNIT_ENTRY]: AIAssistantUnitEntry,
};

const AIAssistantDialog = () => {
  const { isXsScreen, isSmScreen } = useScreenSize();
  const {
    isAssistantOpen,
    closeAssistant,
    isExtended,
    toggleExtended,
    activeView,
    displayMode,
  } = useContext(aiAssistantContext);
  const { isTextEditorOpen } = useContext(aiAssistantEditorContext);
  const user = useSelector(selectUser);
  const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
  const responsiveDialogStyles = isXsScreen ? {
    top: 0,
    right: 0,
    width: '100%',
    maxHeight: '100%',
  } : {
    top: INITIAL_POSITION.top,
    right: INITIAL_POSITION.right,
    width: isTextEditorOpen ? DIALOG_WIDTH.SLIM + 825 : isExtended ? DIALOG_WIDTH.EXTENDED : DIALOG_WIDTH.SLIM,
    maxWidth: isTextEditorOpen ? DIALOG_WIDTH.SLIM + 825 : isExtended ? DIALOG_WIDTH.EXTENDED : DIALOG_WIDTH.SLIM,
    maxHeight: `calc(100% - ${INITIAL_POSITION.top}px - 16px)`,
  };
  
  useEffect(() => {
    if (isSmScreen && isExtended) {
      toggleExtended();
    }
  }, [isExtended, toggleExtended, isSmScreen]);

  return (
    <AIAssistantEditorProvider variant='dialog'>
      <MuiDialog
        disableScrollLock
        disableEnforceFocus
        data-testid='ai-assistant-dialog'
        open={isAssistantOpen}
        onClose={closeAssistant}
        PaperComponent={isXsScreen ? Paper : AIAssistantDraggable}
        aria-labelledby='draggable-dialog-title'
        hideBackdrop
        sx={{
          zIndex: 1400, 
          pointerEvents: 'none',
        }}
        PaperProps={{
          sx: {
            borderRadius: `${borderRadius.lg} !important`,
            background: backgroundGradients.aiAssistant,
            pointerEvents: 'all',
            position: 'absolute',
            margin: 0,
            boxShadow: boxShadows.elevation24,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            ...responsiveDialogStyles
          }
        }} 
      >
        <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AIAssistantDialogTitle />
          <AIAssistantAlerts />
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <AIAssistantViewRouter activeView={activeView} viewMap={DIALOG_VIEW_MAP} />
          </Box>
        </Box>
      </MuiDialog>
      {isPageMode && (user?.isAiOnlyUser
        ? <AIOnlyFeedbackDialog />
        : <AIAssistantFeedbackDialog />
      )}
    </AIAssistantEditorProvider>
  );
};

export default AIAssistantDialog;
